import { useRef } from "react";
import { ImagePlus, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { Store } from "@/types/models";
import { useStoreLogo } from "../hooks/useStoreLogo";

const ACCEPTED_TYPES = "image/jpeg,image/png,image/webp";

export interface StoreLogoUploaderProps {
  store: Store;
}

export function StoreLogoUploader({ store }: StoreLogoUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { attachLogo, removeLogo, attaching, removing, loading, error } = useStoreLogo();

  const logoSrc = store.logoUrl ?? store.logoThumbnailUrl;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    attachLogo(file);
    event.target.value = "";
  };

  const handleRemove = () => {
    if (!window.confirm("Remover o logo da loja?")) return;
    removeLogo();
  };

  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-sm font-light tracking-wide text-primary-900 dark:text-white">
          Logo da loja
        </h3>
        <p className="text-xs font-light text-primary-500 dark:text-primary-500 mt-1">
          JPEG, PNG ou WebP. Máximo 2 MB. Exibido no catálogo público.
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Erro</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap items-start gap-6">
        <div
          className="h-24 w-24 border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900 flex items-center justify-center overflow-hidden"
          data-testid="store-logo-preview"
        >
          {logoSrc ? (
            <img
              src={logoSrc}
              alt={`Logo de ${store.name}`}
              className="h-full w-full object-contain"
            />
          ) : attaching ? (
            <Loader2 className="h-8 w-8 animate-spin text-primary-400" aria-label="Enviando logo" />
          ) : (
            <span className="text-xs font-light text-primary-400 uppercase tracking-widest">
              Sem logo
            </span>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            className="sr-only"
            data-testid="store-logo-file-input"
            onChange={handleFileChange}
            disabled={loading}
          />

          <Button
            type="button"
            variant="outline"
            disabled={loading}
            onClick={() => fileInputRef.current?.click()}
          >
            {attaching ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ImagePlus className="mr-2 h-4 w-4" />
            )}
            {logoSrc ? "Trocar logo" : "Enviar logo"}
          </Button>

          {logoSrc && (
            <Button
              type="button"
              variant="outline"
              disabled={removing}
              onClick={handleRemove}
              className="text-destructive hover:text-destructive"
            >
              {removing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              Remover logo
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}
